import { ReactNode } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAdminRole } from '@/hooks/useAdminRole';
import { useAuth } from '@/hooks/useAuth';
import { Loader2, ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { logger } from '@/lib/logger';

interface AdminRouteProps {
  children: ReactNode;
}

/**
 * Componente de protección para rutas de administración
 * Permite acceso a usuarios con admin_role: 'superadmin', 'support', 'readonly'
 */
export function AdminRoute({ children }: AdminRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const { hasAdminAccess, isLoading: adminLoading, role } = useAdminRole();
  const navigate = useNavigate();

  // Debug
  logger.info('[AdminRoute] State:', { user: user?.email, hasAdminAccess, adminLoading, role });

  // Si está cargando autenticación, mostrar loader
  if (authLoading || adminLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-luxury-pearl to-white">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#B8860B] mx-auto mb-4" />
          <p className="text-neutral-600">Verificando permisos...</p>
        </div>
      </div>
    );
  }

  // Si no está autenticado, redirigir a login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Si no tiene acceso admin (admin_role es null), mostrar acceso denegado
  if (!hasAdminAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-luxury-pearl to-white p-4">
        <Card className="max-w-md w-full border-red-200">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="p-4 bg-red-100 rounded-full">
                <ShieldAlert className="h-12 w-12 text-red-600" />
              </div>
            </div>
            <CardTitle className="text-2xl text-neutral-900">
              Acceso Denegado
            </CardTitle>
            <CardDescription className="text-base">
              No tienes permisos para acceder al panel de administración
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800 text-center">
                Esta área está restringida solo para administradores del sistema.
                Si crees que deberías tener acceso, contacta al equipo de soporte.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <Button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-luxury-gold text-black hover:bg-luxury-bronze"
              >
                Volver al Dashboard
              </Button>

              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="w-full text-neutral-600 hover:text-[#B8860B]"
              >
                Ir al Inicio
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Si tiene admin_role válido, permitir acceso
  return <>{children}</>;
}
